/* eslint-disable no-nested-ternary */
/* eslint-disable react-hooks/rules-of-hooks */
import { SCWidget } from 'features/podcasts/business/soundcloud';
import React, { ComponentPropsWithRef, FC, useContext, useEffect, useRef, useState } from 'react';
import { Text } from '@components/Text/Text.component';
import { CircleChevronDownIcon } from '@icons/CircleChevronDown.icon';
import { CircleChevronUpIcon } from '@icons/CircleChevronUp.icon';
import { CloseCircleIcon } from '@icons/CloseCircle.icon';
import { useTranslation } from '@locales/useTranslation.hook';
import { Breakpoint, useBreakpointBis } from '@styles/breakpoints';
import { PlcColor } from '@styles/colors';
import { usePlcRouter } from '@utils/customHooks';
import { urlEnum } from '@utils/url';
import classnames from 'classnames';
import { GeneralContext } from 'features/business/app.contexts';
import styles from './PodcastsPlayer.module.scss';

const APP_BANNER_HEIGHT = 56;

export type PodcastsPlayerProps = ComponentPropsWithRef<'section'> & {
  isMiniPlayerVisible: boolean;
};

function playerTop() {
  let elementTop: number;
  switch (useBreakpointBis()) {
    case Breakpoint.s:
      elementTop = 115;
      break;
    case Breakpoint.xs:
    case Breakpoint.m:
      elementTop = 120;
      break;
    case Breakpoint.l:
      elementTop = 175;
      break;
    default:
      elementTop = 250;
  }
  return elementTop;
}

const PodcastsPlayer: FC<PodcastsPlayerProps> = ({ isMiniPlayerVisible }) => {
  const { t } = useTranslation(['podcasts']);
  const { setIsPlaying, showAppBanner } = useContext(GeneralContext);

  const iframeRef = useRef(null);
  const widgetRef = useRef<SCWidget | null>(null);
  const rootRef = useRef(null);
  const [headerPageHeight, setHeaderPageHeight] = useState(0);
  const [isHidden, setIsHidden] = useState(false);

  const { route } = usePlcRouter();
  const isPodcastsPage = route === urlEnum.podcasts;

  const hidePlayer = () => {
    setIsHidden((prev) => !prev);
  };

  const closePlayer = () => {
    const widget = widgetRef.current;
    if (widget && typeof widget.seekTo === 'function' && typeof widget.pause === 'function') {
      widget.seekTo(0);
      widget.pause();
    }
    setIsPlaying(false);
  };

  useEffect(() => {
    const loadWidgetScript = async () => {
      if (typeof window === 'undefined' || !iframeRef.current) return;

      if (!window.SC) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://w.soundcloud.com/player/api.js';
          script.onload = resolve;
          script.onerror = reject;
          document.body.appendChild(script);
        });
      }

      if (iframeRef.current && window.SC) {
        const widget = window.SC.Widget(iframeRef.current);
        widgetRef.current = widget;

        widget.bind(window.SC.Widget.Events.PLAY, () => {
          setIsPlaying(true);
        });
      }
    };

    loadWidgetScript();

    return () => {
      const widget = widgetRef.current;
      if (widget && window.SC && typeof widget.unbind === 'function') {
        try {
          widget.unbind(window.SC.Widget.Events.PLAY);
        } catch (e) {
          console.error('SC unbind error', e);
        }
      }
    };
  }, [setIsPlaying]);

  const rootClassname = classnames(
    styles.playerContainer,
    isMiniPlayerVisible ? styles['mini-player'] : isPodcastsPage ? styles['full-player'] : styles['hidden-player']
  );

  const iframeClassname = classnames(
    styles.iframe,
    isMiniPlayerVisible ? styles['mini-iframe'] : styles['full-iframe'],
    isHidden && !isPodcastsPage ? styles['hidden-iframe'] : ''
  );

  useEffect(() => {
    function updateTextSEOHeight() {
      const header = document.getElementById('podcasts-text-seo');
      if (header) {
        setHeaderPageHeight(header.offsetHeight);
      }
    }

    updateTextSEOHeight();
    window.addEventListener('resize', updateTextSEOHeight);
    return () => window.removeEventListener('resize', updateTextSEOHeight);
  }, [isPodcastsPage]);

  return (
    <section
      ref={rootRef}
      className={rootClassname}
      style={{
        top:
          !isMiniPlayerVisible && isPodcastsPage
            ? `calc(${headerPageHeight}px + ${playerTop()}px + ${showAppBanner ? APP_BANNER_HEIGHT : 0}px)`
            : '',
      }}
    >
      <iframe
        ref={iframeRef}
        allow="autoplay"
        className={iframeClassname}
        height="400"
        src="https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/playlists/2012588232&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true&visual=true"
        title="iframe"
      />

      {!isPodcastsPage && (
        <div className={styles.footer}>
          <Text variant="tiny">{t('podcasts.playlist.title')}</Text>
          <div className={styles.buttons} onClick={hidePlayer}>
            {isHidden ? (
              <CircleChevronUpIcon fillColor={PlcColor.GREEN} height={20} width={20} />
            ) : (
              <CircleChevronDownIcon fillColor={PlcColor.GREEN} height={20} width={20} />
            )}
          </div>
          <div className={styles.buttons} onClick={closePlayer}>
            <CloseCircleIcon fillColor={PlcColor.GREEN} height={24} width={24} />
          </div>
        </div>
      )}
    </section>
  );
};

export { PodcastsPlayer };
